import { DEFAULT_LANGUAGE } from "../../../config/i18n";
import { SupportedLanguage } from "../../../interfaces";
import countries from "i18n-iso-countries";
import ar from "i18n-iso-countries/langs/ar.json";
import de from "i18n-iso-countries/langs/de.json";
import en from "i18n-iso-countries/langs/en.json";
import es from "i18n-iso-countries/langs/es.json";
import fr from "i18n-iso-countries/langs/fr.json";
import it from "i18n-iso-countries/langs/it.json";
import nl from "i18n-iso-countries/langs/nl.json";
import pt from "i18n-iso-countries/langs/pt.json";

// Exhaustive: TS errors here if SupportedLanguage gains a new member
const COUNTRY_LOCALES = {
  ar,
  de,
  en,
  es,
  fr,
  it,
  nl,
  pt,
} satisfies Record<SupportedLanguage, countries.LocaleData>;

for (const locale of Object.values(COUNTRY_LOCALES)) {
  countries.registerLocale(locale);
}

export const POPULAR_COUNTRY_CODES = [
  "FR",
  "GB",
  "US",
  "DE",
  "DZ",
  "ES",
  "MA",
  "BE",
  "SN",
  "CA",
  "CI",
  "NG",
  "CH",
  "SG",
  "ZA",
  "IN",
  "PK",
  "TN",
  "NO",
  "NL",
  "AU",
];

function countryCodeToFlag(code: string): string {
  const REGIONAL_A = 0x1_f1_e6;
  const CHAR_A = 65;
  return [...code.toUpperCase()]
    .map((c) => {
      const cp = c.codePointAt(0);
      return cp !== undefined
        ? String.fromCodePoint(REGIONAL_A - CHAR_A + cp)
        : "";
    })
    .join("");
}

export function getCountriesList(
  language: SupportedLanguage = DEFAULT_LANGUAGE
) {
  return countries.getNames(language, { select: "official" });
}

export function getCountriesListSortedByPopularity(
  language: SupportedLanguage = DEFAULT_LANGUAGE
) {
  const countriesList = getCountriesList(language);
  const countryOptions = Object.entries(countriesList)
    .map(([value, name]) => {
      const flag = countryCodeToFlag(value);
      // `label` keeps the flag-prefixed form for the Select consumer; `name`
      // and `flag` are split out for callers that render them separately.
      return { value, name, flag, label: `${flag} ${name}` };
    })
    .sort((a, b) => {
      // If a favourite, comes before b
      if (
        POPULAR_COUNTRY_CODES.includes(a.value) &&
        !POPULAR_COUNTRY_CODES.includes(b.value)
      ) {
        return -1;
      }
      // If b favourite, comes before a
      if (
        !POPULAR_COUNTRY_CODES.includes(a.value) &&
        POPULAR_COUNTRY_CODES.includes(b.value)
      ) {
        return 1;
      }
      // Sort non favorites alphabetically
      if (
        !POPULAR_COUNTRY_CODES.includes(a.value) &&
        !POPULAR_COUNTRY_CODES.includes(b.value)
      ) {
        return a.label.localeCompare(b.label);
      }
      // If both favorites, leave them in their original order in POPULAR_COUNTRY_CODES
      return (
        POPULAR_COUNTRY_CODES.indexOf(a.value) -
        POPULAR_COUNTRY_CODES.indexOf(b.value)
      );
    });
  return countryOptions;
}
